import React from 'react';

import type { CSSProperties, SVGProps } from 'react';

export type IconProps = {
  // Due to inconsistency of icon paths, color default value has been removed.
  // If an icon has multiple paths, use className to pick a path to paint.
  color?: string;
  size?: number | string;
} & SVGProps<SVGSVGElement>;

type IconDimension = NonNullable<CSSProperties['width']>;

const getDisplayName = (WrappedComponent: any) =>
  WrappedComponent.displayName || WrappedComponent.name || 'Component';

function withIconProps(
  WrappedComponent: any,
  options: {
    // for some icons which is not a square, we don't want to put a default size like 24x24.
    // we can either turn off shouldSetDefaultSize to not change the icon's size, or set
    // defaultWidth and defaultHeight manually to overwrite the size
    shouldSetDefaultSize?: boolean;
    defaultWidth?: IconDimension;
    defaultHeight?: IconDimension;
    defaultColor?: string;
  } = {
    shouldSetDefaultSize: true,
    defaultWidth: 24,
    defaultHeight: 24,
  },
) {
  const resolvedOptions = {
    shouldSetDefaultSize: true,
    defaultWidth: 24,
    defaultHeight: 24,
    ...options,
  };

  function NewComponent(props: IconProps) {
    const {
      size,
      color = resolvedOptions.defaultColor || 'currentColor',
      className,
      ...rest
    } = props;

    const style: Pick<CSSProperties, 'height' | 'width'> = {
      height: size ?? props.height,
      width: size ?? props.width,
    };

    if (resolvedOptions.shouldSetDefaultSize) {
      style.height = style.height ?? resolvedOptions.defaultHeight;
      style.width = style.width ?? resolvedOptions.defaultWidth;
    }

    return <WrappedComponent {...rest} className={className} color={color} style={style} />;
  }

  NewComponent.displayName = `withIconProps(${getDisplayName(WrappedComponent)})`;

  return NewComponent;
}

export default withIconProps;
